// Updated AoaButton.tsx - now accepts external fetch function
import "./AoaButton.scss";
import {
  LoadingSpinner,
  RYAN_DATASOURCES,
  RyanLoader,
  SourceKey,
  SOURCES,
} from "@repo/ping-react-js";
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@repo/ping-design";
import { useEffect, useMemo } from "react";
import { useState, useCallback } from "react";
import toast from "react-hot-toast";
import { APPEND_ADDITIONAL_SOURCES } from "../../../utils/maps-utils";

type AoaButtonProps = {
  sovid?: string | null;
  label: string;
  requestData: any;
  source: SourceKey;
  disabled?: boolean;
  onFetchClicked?: () => void;
  onErrorChanged?: (errorMessage: string) => void;
  buttonRefs: React.MutableRefObject<Record<string, HTMLButtonElement | HTMLAnchorElement | null>>;
  isPLI?: boolean;
  buildingId?: string;
  pliId?: string;
  // New prop: external fetch function that handles the query
  triggerAoAQuery: (args: any) => Promise<any>;
  onSetAoAResponse?: (data: any) => void;
  isFetching: boolean;
  integrationSettings?: Record<string, any>;
};

const AoaButton = ({
  source,
  requestData,
  label,
  disabled = false,
  onFetchClicked,
  onErrorChanged,
  buttonRefs,
  isPLI,
  buildingId,
  pliId,
  triggerAoAQuery,
  onSetAoAResponse,
  isFetching,
  sovid,
  integrationSettings,
}: AoaButtonProps) => {
  const sovidToUse = useMemo(() => {
    if (isPLI) {
      if (pliId) {
        return pliId;
      } else {
        return null;
      }
    } else {
      return sovid;
    }
  }, [isPLI, pliId, sovid]);

  const [internalId, setInternalId] = useState<string | null>(null);

  const extractError = useCallback(
    (locationData: Record<string, any>) => {
      const DEFAULT_ERROR_MESSAGE = "Something went wrong";

      if (!locationData?.error_message) {
        return DEFAULT_ERROR_MESSAGE;
      }

      if (source === SOURCES.TIGERRISKAIR || source === SOURCES.TIGERRISKRMS) {
        const tigerRegex = /<faultstring>(.*?)<\/faultstring>/;
        const match = locationData?.error_message.match(tigerRegex);

        return match ? match[1] : DEFAULT_ERROR_MESSAGE;
      } else {
        return locationData?.error_message || DEFAULT_ERROR_MESSAGE;
      }
    },
    [source],
  );

  useEffect(() => {
    let toastId: string | undefined;

    if (isFetching && RYAN_DATASOURCES.includes(source)) {
      toastId = toast.custom(
        (t) => (
          <div style={{ position: "relative", top: "44px" }}>
            <RyanLoader />
          </div>
        ),
        {
          duration: Infinity,
          style: {
            minWidth: "300px",
          },
          position: "top-center",
        },
      );
    } else if (toastId) {
      toast.dismiss(toastId);
    }

    return () => {
      if (toastId) toast.dismiss(toastId);
    };
  }, [isFetching, source]);

  const handleFetchClick = async () => {
    const resolvedLat = requestData?.latitude ?? requestData?.lat;
    const resolvedLng = requestData?.longitude ?? requestData?.lng;

    if (disabled || resolvedLat == null || resolvedLng == null || !sovidToUse || isFetching) {
      onErrorChanged?.("Missing building coordinates");
      return;
    }

    onFetchClicked?.();

    try {
      const result = await triggerAoAQuery({
        sov_id: sovidToUse,
        item_key: buildingId,
        lat: resolvedLat,
        lng: resolvedLng,
        source: source,
        ...requestData,
      });

      const sourcesToCheck = APPEND_ADDITIONAL_SOURCES(
        source,
        integrationSettings,
      );
      let hasErrors = false;
      const errorMessages: string[] = [];
      const isMapped = sourcesToCheck.length > 1;
      for (const src of sourcesToCheck) {
        const locData = result?.data?.location_data?.[src];
        if (locData && locData.is_success !== true) {
          hasErrors = true;
          if (isMapped) {
            errorMessages.push(`${src}:\n${extractError(locData)}`);
          } else {
            errorMessages.push(extractError(locData));
          }
        }
      }

      const errorMessage = errorMessages.length > 0 ? errorMessages.join("\n\n") : null;

      const isDifferentRequest = internalId !== result?.data?.id;
      if (isDifferentRequest) {
        setInternalId(result?.data?.id);
      }

      if (hasErrors && errorMessage) {
        if (isDifferentRequest) {
          onErrorChanged?.(errorMessage);
        }
      }

      if (result.data) {
        onSetAoAResponse?.(result.data);
      }
    } catch (error) {
      console.error("AoA query failed:", error);
      const apiError = (error as { data?: unknown })?.data;
      const errorMessage =
        typeof apiError === "string"
          ? apiError
          : (apiError as { message?: string; detail?: string } | undefined)?.message ||
            (apiError as { message?: string; detail?: string } | undefined)?.detail;
      onErrorChanged?.(errorMessage || "Failed to fetch data");
    }
  };

  return (
    <Tooltip tooltipRole="label" placement="top">
      <TooltipTrigger asChild disabled={disabled}>
        <Button
          label={
            <div className="AoaButton__ButtonContent">
              <span>{isFetching ? "Fetching" : label}</span>
            </div>
          }
          ref={(el: HTMLButtonElement | null) => {
            buttonRefs.current[source] = el;
          }}
          iconName={
            !isFetching ? (
              "RefreshCcw"
            ) : (
              <div className="AoaButton__SpinnerOverlay">
                <LoadingSpinner size="xs" color="#1E1F36" />
              </div>
            )
          }
          variant="outline"
          size="sm"
          onClick={handleFetchClick}
        />
      </TooltipTrigger>
      <TooltipContent>
        {disabled ? "Required fields are not available" : label}
      </TooltipContent>
    </Tooltip>
  );
};

export default AoaButton;
